import { type BookingSettings, type Group, type LocationQueryField, useLocationQuery } from '@cheqroom/api';
import { DateTime } from '@cheqroom/date-time';
import { useCallback, useMemo } from 'react';

import { createBusinessHoursSnappers, getHoursForDay, resolveOpeningHours } from './opening-hours.utils';
import {
	type BookingDates,
	type BusinessHoursSnappers,
	resolveDueComparisonDate,
	resolveSnapBookingDates,
} from './snap-booking-dates.utils';

/**
 * The workspace facts opening hours depend on, picked from the python-api `Group`. Its `businessHours`
 * come as python-api rows (`dayOfWeek`, 0 = Monday), like a location's; the hook converts them once and
 * returns ISO-weekday rows (`isoWeekday`, 1 = Monday) — see `resolveOpeningHours`.
 */
export type OpeningHoursWorkspace = Pick<Group, 'businessHours'> & {
	limits: Pick<Group['limits'], 'allowLocationBusinessHours'>;
	profile: Pick<Group['profile'], 'useHours'>;
};

interface Options {
	/** The location whose hours apply. Without it, the workspace hours apply. */
	locationId?: string | null;
	locationFields?: readonly LocationQueryField[];
	/**
	 * The location whose booking settings decide `useHours`. Defaults to `locationId`; a caller can pass
	 * another one (web passes the nav location when there is no `locationId`).
	 */
	bookingSettingsLocationId?: string | null;
	/**
	 * `null` when the caller has none (e.g. an Info component without a workspace in its config): no workspace
	 * hours and hour precision, so overdue compares with the stored time.
	 */
	workspace: OpeningHoursWorkspace | null;
}

/**
 * Hour or day precision for bookings: a location with booking settings decides by its
 * `timeOptionInterval`, otherwise the workspace's `useHours` applies.
 */
export const resolveUseHours = (
	location: { bookingSettings?: Pick<BookingSettings, 'timeOptionInterval'> | null } | null | undefined,
	workspaceUseHours: boolean
): boolean =>
	location?.bookingSettings != null ? location.bookingSettings.timeOptionInterval != null : workspaceUseHours;

/**
 * Opening hours for a location, or the workspace hours when `locationId` is absent, plus the
 * business-day getters and date snapping built on them.
 */
export const useOpeningHours = ({
	locationId,
	locationFields,
	bookingSettingsLocationId = locationId,
	workspace,
}: Options) => {
	// When one location gives both the hours and the booking settings (the default), fetch it once:
	// a restricted `locationFields` gets `bookingSettings` added so `useHours` can read it.
	const isSameLocation = bookingSettingsLocationId === locationId;
	const fields = useMemo<readonly LocationQueryField[] | undefined>(
		() =>
			isSameLocation && locationFields && !locationFields.includes('bookingSettings')
				? [...locationFields, 'bookingSettings']
				: locationFields,
		[isSameLocation, locationFields]
	);
	const { data, isFetched, refetch } = useLocationQuery({ id: locationId ?? '', fields }, { enabled: !!locationId });
	const {
		data: otherBookingSettingsLocation,
		isFetched: isOtherBookingSettingsLocationFetched,
		refetch: refetchOtherBookingSettingsLocation,
	} = useLocationQuery(
		{ id: bookingSettingsLocationId ?? '' },
		{ enabled: !!bookingSettingsLocationId && !isSameLocation }
	);

	const workspaceUseHours = workspace?.profile.useHours ?? true;
	const useHours = resolveUseHours(isSameLocation ? data : otherBookingSettingsLocation, workspaceUseHours);

	const groupBusinessHours = workspace?.businessHours;
	const allowLocationBusinessHours = workspace?.limits.allowLocationBusinessHours ?? false;

	/**
	 * A location-scoped caller has asked for hours we do not have yet. Snapping now would use
	 * group hours (wrong location) — and treating it as "unconfigured" would snap to calendar
	 * midnight/23:59, which is worse. Async callers should await `snapDatesWhenResolved`.
	 */
	const isResolvingLocationHours = Boolean(locationId) && allowLocationBusinessHours && !isFetched;
	/** Likewise for the booking settings that decide `useHours`: until they load, `useHours` is the workspace's. */
	const isResolvingBookingSettings =
		Boolean(bookingSettingsLocationId) && !(isSameLocation ? isFetched : isOtherBookingSettingsLocationFetched);

	const openingHours = useMemo(
		() => resolveOpeningHours(data?.businessHours, groupBusinessHours, allowLocationBusinessHours),
		[data, groupBusinessHours, allowLocationBusinessHours]
	);

	const hoursForDay = useCallback((day: DateTime) => getHoursForDay(openingHours, day), [openingHours]);

	const businessHoursSnappers = useMemo<BusinessHoursSnappers>(
		() => createBusinessHoursSnappers(openingHours),
		[openingHours]
	);

	const getBusinessDayEnd = useCallback(
		(day?: DateTime | null) => (day ? businessHoursSnappers.endOfDay(day) : DateTime.now().endOfDay()),
		[businessHoursSnappers]
	);

	const getComparisonDueDate = useCallback(
		(date?: DateTime | null) => resolveDueComparisonDate(date, useHours, getBusinessDayEnd),
		[useHours, getBusinessDayEnd]
	);

	const snapDates = useCallback(
		// `force` snaps to business hours even for hour-precision workspaces. Use it
		// for day-precision selections (e.g. month-view clicks) where there is no
		// meaningful time of day to preserve.
		(dates: BookingDates, { force = false }: { force?: boolean } = {}): BookingDates =>
			resolveSnapBookingDates(dates, useHours, businessHoursSnappers, { force }),
		[useHours, businessHoursSnappers]
	);

	/**
	 * Snap once the location's own hours and the booking settings that decide the precision are known.
	 *
	 * Use from async handlers (calendar create) so a click landing before the location queries
	 * settle snaps with that location's hours and precision instead of the group's and workspace's.
	 * Falls back to the sync snap when nothing is pending. `refetch` resolves before React has
	 * necessarily re-rendered, so this snaps from what it returns, not from the render closure.
	 */
	const snapDatesWhenResolved = useCallback(
		async (dates: BookingDates, { force = false }: { force?: boolean } = {}): Promise<BookingDates> => {
			if (!isResolvingLocationHours && !isResolvingBookingSettings) return snapDates(dates, { force });

			const refetchHoursLocation = isResolvingLocationHours || (isSameLocation && isResolvingBookingSettings);
			const refetchBookingSettingsLocation = !isSameLocation && isResolvingBookingSettings;
			const [hoursLocation, bookingSettingsLocation] = await Promise.all([
				refetchHoursLocation ? refetch().then((result) => result.data) : data,
				refetchBookingSettingsLocation
					? refetchOtherBookingSettingsLocation().then((result) => result.data)
					: otherBookingSettingsLocation,
			]);

			const hours = resolveOpeningHours(
				hoursLocation?.businessHours,
				groupBusinessHours,
				allowLocationBusinessHours
			);
			const resolvedUseHours = resolveUseHours(
				isSameLocation ? hoursLocation : bookingSettingsLocation,
				workspaceUseHours
			);
			return resolveSnapBookingDates(dates, resolvedUseHours, createBusinessHoursSnappers(hours), { force });
		},
		[
			isResolvingLocationHours,
			isResolvingBookingSettings,
			isSameLocation,
			snapDates,
			refetch,
			refetchOtherBookingSettingsLocation,
			data,
			otherBookingSettingsLocation,
			groupBusinessHours,
			allowLocationBusinessHours,
			workspaceUseHours,
		]
	);

	return {
		useHours,
		location: data,
		openingHours,
		getHoursForDay: hoursForDay,
		getBusinessDayEnd,
		getComparisonDueDate,
		snapDatesWhenResolved,
	};
};
